import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { AlertTriangle, CheckCircle, Clock, MapPin, Activity } from 'lucide-react';

const DemoAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');

  const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/demo`;

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (locations.length > 0) {
      fetchAlerts();
    }
  }, [selectedLocation, selectedSeverity, locations]);

  const fetchLocations = async () => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1200)); // Augmenté à 1.2s
      const response = await fetch(`${apiUrl}/locations`);
      if (response.ok) {
        const data = await response.json();
        setLocations(Array.isArray(data) ? data : []);
      } else if (response.status === 429) {
        console.warn('Rate limited, retrying...');
        setTimeout(fetchLocations, 5000);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1800)); // Augmenté à 1.8s
      const response = await fetch(`${apiUrl}/alerts`);
      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement des alertes...</p>
        </div>
      </div>
    );
  }

  // Filter alerts by location and severity
  const filteredAlerts = alerts.filter(alert => {
    if (selectedLocation !== 'all' && alert.location !== locations.find(l => l.id === selectedLocation)?.name) {
      return false;
    }
    if (selectedSeverity !== 'all' && alert.severity !== selectedSeverity) {
      return false;
    }
    return true;
  });

  const activeAlerts = filteredAlerts.filter(a => !a.acknowledged);
  const criticalCount = filteredAlerts.filter(a => a.severity === 'critical' && !a.acknowledged).length;

  return (
    <div className="p-3 sm:p-6 space-y-6">
      <div className="bg-gradient-to-br from-red-600 via-orange-600 to-amber-600 rounded-xl shadow-2xl p-6 sm:p-8 text-white">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-10 w-10" />
          <div>
            <h1 className="text-3xl font-bold">Alertes Système</h1>
            <p className="text-red-50 text-lg">
              {activeAlerts.length} alertes actives
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold"
          >
            <option value="all">Toutes les localisations</option>
            {locations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.name}</option>
            ))}
          </select>

          <select
            value={selectedSeverity}
            onChange={(e) => setSelectedSeverity(e.target.value)}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold"
          >
            <option value="all">Toutes les gravités</option>
            <option value="critical">Critiques</option>
            <option value="warning">Avertissements</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {filteredAlerts.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <p className="text-xl font-bold text-gray-900 dark:text-white mb-2">Aucune alerte</p>
          </div>
        ) : (
          filteredAlerts.map(alert => (
            <div
              key={alert.id}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg border-l-4 p-6 ${
                alert.severity === 'critical' ? 'border-red-500' : 'border-yellow-500'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    alert.severity === 'critical'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {alert.severity === 'critical' ? 'CRITIQUE' : 'ATTENTION'}
                  </span>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-2">
                    {alert.message}
                  </h3>
                  <div className="flex gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {alert.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Activity className="h-4 w-4" />
                      {alert.deviceId}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DemoAlerts;
